/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description

  This class is the next satellite panel container. It displays the next beam, ttho, and handoff distance.
 */

import React from 'react';
import { TerminalDetailsContext } from './NextSatellitePanelContainerWrapper';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../utils/StringUtils';
import { BEAM_TYPE, getBeamTypeFromValue } from '../../../../utils/BeamUtils';
import { formatTtho } from '../../../../utils/DateTimeUtils';

import isNil from 'lodash/isNil';
import TruncateWithTooltip from '../../../common/TruncateWithTooltip';

const KA_SPOT_BEAM_HANDOVER_ICON_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 26 26">
    <path fill="none" d="M0 0h26v26H0V0z" />
    <circle cx="13" cy="13" r="13" fill="#ffffff" />
    <circle cx="13" cy="13" r="12" fill="#5EA215" />
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4">
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M15 4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" />
      <path d="M3 12c0-2.61 1.67-4.83 4-5.65V4.26C3.55 5.15 1 8.27 1 12s2.55 6.85 6 7.74v-2.09c-2.33-.82-4-3.04-4-5.65z" />
    </svg>
  </svg>
);

const HANDOVER_ICON_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 26 26">
    <path fill="none" d="M0 0h26v26H0V0z" />
    <circle cx="13" cy="13" r="13" fill="#ffffff" />
    <circle cx="13" cy="13" r="12" fill="#FFB029" />
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4">
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" />
    </svg>
  </svg>
);

const NETWORK_HANDOVER_ICON_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 26 26">
    <path fill="none" d="M0 0h26v26H0V0z" />
    <circle cx="13" cy="13" r="13" fill="#ffffff" />
    <circle cx="13" cy="13" r="12" fill="#FFB029" />
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4">
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" />
    </svg>
  </svg>
);

function NextSatellitePanelContainer() {
  const renderHandoverHeader = (terminalDetails: TerminalDetails) => {
    let currentBeamType = getBeamTypeFromValue(terminalDetails.beamName);
    let nextBeamType = getBeamTypeFromValue(terminalDetails.nextBeam);
    let header = {
      symbol: {},
      text: '',
    };
    if (currentBeamType === BEAM_TYPE.KA && nextBeamType === BEAM_TYPE.KA) {
      header.symbol = KA_SPOT_BEAM_HANDOVER_ICON_SVG;
      header.text = 'Upcoming Ka Spot Beam Handover';
    } else if (currentBeamType !== nextBeamType) {
      header.symbol = NETWORK_HANDOVER_ICON_SVG;
      if (currentBeamType === BEAM_TYPE.KU) {
        header.text = 'Upcoming Ku to Ka Handover';
      } else {
        header.text = 'Upcoming Ka to Ku Handover';
      }
    } else {
      header.symbol = HANDOVER_ICON_SVG;
      header.text = 'Upcoming Ku to Ku Handover';
    }
    return (
      <React.Fragment>
        <div className="icon">{header.symbol}</div>
        {header.text}
      </React.Fragment>
    );
  };

  return (
    <TerminalDetailsContext.Consumer>
      {(terminalDetails: TerminalDetails) => (
        <React.Fragment>
          <div className="next__satellite__container">
            <div className="data_wrapper" data-test="next_satellite_item__data_wrapper">
              <div className="header">{renderHandoverHeader(terminalDetails)}</div>
              <div className="background__line" />
              <div className="label next_beam">Next Beam</div>
              <div className="label ttho">TTHO</div>
              <div className="label dth">DTH</div>
              <div className="value next_beam" data-test="next_satellite_item__next_beam">
                {!isNil(terminalDetails.nextBeam) ? (
                  <TruncateWithTooltip text={`${terminalDetails.nextBeam}`} tooltipMaxWidthPx={275} />
                ) : (
                  `${EMPTY_FIELD_STRING_PLACEHOLDER}`
                )}
              </div>
              <div className="value ttho" data-test="next_satellite_item__ttho">
                {terminalDetails && !isNil(terminalDetails.tthoSeconds)
                  ? formatTtho(terminalDetails.tthoSeconds)
                  : EMPTY_FIELD_STRING_PLACEHOLDER}{' '}
              </div>
              <div className="value dth" data-test="next_satellite_item__dth">
                <React.Fragment>
                  {terminalDetails &&
                  terminalDetails.handoverDistanceMiles !== undefined &&
                  terminalDetails.handoverDistanceMiles !== null
                    ? Math.floor(terminalDetails.handoverDistanceMiles) + ' mi'
                    : EMPTY_FIELD_STRING_PLACEHOLDER}
                </React.Fragment>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </TerminalDetailsContext.Consumer>
  );
}

export default NextSatellitePanelContainer;
